import { Injectable, OnDestroy } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState, IHttpConnectionOptions, LogLevel } from '@microsoft/signalr';
import * as signalR from '@microsoft/signalr'
import { JwtUtils } from '../../auth/utilities/jwtUtils';
import { ConsoleLogger } from '../../core/utilities/console-logger';
import { IndividualConfig, ToastrService } from 'ngx-toastr';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Roles } from '../../auth/roles.enum';
import { HubReconnectScheduler } from '../../core/utilities/hub-reconnect-scheduler';

@Injectable()
export class SignalRService implements OnDestroy {
  private static instance: SignalRService;
  connection: HubConnection;
  messages: Array<string> = [];
  private isConnected = false;
  private isInitializing = false;
  private isShuttingDown = false;
  private reconnectScheduler = new HubReconnectScheduler('MessageHub', () => this.connect());

  constructor(private toastrService: ToastrService) {
    if (SignalRService.instance) {
      ConsoleLogger.logInfo('Reusing existing MessageHub instance');
      return SignalRService.instance;
    }
    
    SignalRService.instance = this;
  }
  
  ngOnDestroy() {
    this.disconnect();
  }

  initialize() {
    if (this.isConnected || this.isInitializing) {
      ConsoleLogger.logInfo('MessageHub connection already established or initializing, skipping initialization');
      return;
    }
    
    this.isInitializing = true;
    this.isShuttingDown = false;
    
    const connectionOptions: IHttpConnectionOptions = {
      skipNegotiation: true,
      transport: signalR.HttpTransportType.WebSockets,
      accessTokenFactory: () => JwtUtils.accessToken
    };

    this.connection = new HubConnectionBuilder()
      .withUrl(environment.signalRUrl, connectionOptions)
      .withAutomaticReconnect([0, 1000, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Information)
      .build();

    // 2x the server's 30s KeepAliveInterval
    this.connection.serverTimeoutInMilliseconds = 60000;

    this.registerEvents();
    this.connect();
  };

  disconnect() {
    this.isShuttingDown = true;
    this.reconnectScheduler.cancel();

    if (!this.connection) {
      return;
    }
    
    ConsoleLogger.logInfo('MessageHub ConnectionStatus', this.connection.state.toString());
    if (this.connection.state === HubConnectionState.Disconnected) {
      this.isConnected = false;
      this.isInitializing = false;
      return;
    }

    this.connection.stop()
      .then(() => {
        this.isConnected = false;
        this.isInitializing = false;
        ConsoleLogger.logInfo('MessageHub connection stopped.');
      })
      .catch(err => {
        this.isConnected = false;
        this.isInitializing = false;
        ConsoleLogger.logInfo('Error stopping MessageHub connection:', err);
      });
  }

  connect() {
    if (!this.connection) {
      this.initialize();
      return;
    }
    
    if (this.connection.state === HubConnectionState.Connected) {
      this.isConnected = true;
      this.isInitializing = false;
      return;
    }

    this.connection
      .start()
      .then(() => {
        ConsoleLogger.logInfo("Connected to MessageHub")
        this.isInitializing = false;

        if (this.isShuttingDown) {
          this.connection.stop()
            .catch(err => ConsoleLogger.logInfo('Error stopping MessageHub after disconnect:', err));
          return;
        }

        this.isConnected = true;
        this.reconnectScheduler.reset();
        this.joinGroups();
      }).catch((err) => {
        console.warn('Error while starting MessageHub connection:', err);
        this.isConnected = false;
        this.isInitializing = false;

        if (!this.isShuttingDown) {
          this.reconnectScheduler.schedule();
        }
      });
  }

  private joinGroups() {
    this.connection.invoke('JoinAuthenticatedUsers');

    if(JwtUtils.hasRoleByName(Roles.Developer)) {
      this.connection.invoke('JoinDevelopersGroup')
    }
  }

  broadcastMessageToAll(message: string, disableTimeout: boolean = false) {
    this.connection.invoke("BroadcastToAuthenticated", message, disableTimeout);
  }

  broadcastMessageToDevelopers(message: string, disableTimeout: boolean = false) {
    this.connection.invoke("BroadcastToDevelopers", message, disableTimeout);
  }

  private registerEvents() {
    this.connection.onclose(() => {
      ConsoleLogger.logInfo("Lost MessageHub connection");
      this.isConnected = false;
      this.isInitializing = false;

      if (!this.isShuttingDown) {
        this.reconnectScheduler.schedule();
      }
    });

    this.connection.onreconnecting(() => {
      ConsoleLogger.logInfo("Reconnecting to MessageHub");
      this.isConnected = false;
    });

    this.connection.onreconnected(() => {
      ConsoleLogger.logInfo("Reconnected to MessageHub");
      this.isConnected = true;
      this.reconnectScheduler.reset();
      this.joinGroups();
    });

    this.connection.off("UserConnected");
    this.connection.on("UserConnected", (user) => {
      ConsoleLogger.logInfo('User Connected', `${user} connected`)
    });

    this.connection.off("UserDisconnected");
    this.connection.on("UserDisconnected", (user) => {
      ConsoleLogger.logInfo('User Disconnected', `${user} disconnected`)
    });

    this.connection.off("BroadcastReceived");
    this.connection.on("BroadcastReceived", (user: string, message: string, disableTimeout: boolean, groupName: string) => {
      ConsoleLogger.logInfo('Broadcast Received', `From: ${user}; Message: ${message}:To: ${groupName}; TimeoutDisabled: ${disableTimeout}`);

      const toastConfig = {
        enableHtml: true,
        disableTimeOut: disableTimeout,
        progressBar: !disableTimeout
      };

      if (!groupName) {
        this.toastrService.info(message, `From ${user}`, toastConfig);
        return;
      }

      const toastrMessage = `<div class="mt-2"><strong><u>From [${user}]</u></strong><br>${message}</div>`
      this.toastrService.info(toastrMessage, `To [${groupName}]`, toastConfig);
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/signalR/signalR.service.ts